import type { Message } from "../../core/types";

const isToolCallChip = (message: Message): boolean =>
  message.role === "assistant" && message.metadata?.field_name === "tool_call";

/**
 * Decides where the message list should scroll when `messages` just gained a
 * renderable last entry (LAS-1381).
 *
 * Returns the id of the message whose top belongs at the top of the message
 * area, or null when the list should pin to the bottom as it always did.
 *
 * Only a streaming-capable assistant reply earns an anchor: a long answer is
 * read from its start, not from its end. The anchor is the reader's own
 * question when nothing but tool-call chips sits between the two, so the
 * question stays in view above the answer; otherwise it is the answer itself.
 * User messages, tool-call chips, rating prompts and unrendered replies keep
 * the bottom pin.
 */
export const findReadingAnchor = (messages: Message[]): number | null => {
  const last = messages[messages.length - 1];
  if (last === undefined || last.role !== "assistant" || last.contents === null) return null;
  const fieldName = last.metadata?.field_name;
  if (fieldName === "tool_call" || fieldName === "submit_rating") return null;

  for (let i = messages.length - 2; i >= 0; i--) {
    const earlier = messages[i];
    if (earlier.contents === null || isToolCallChip(earlier)) continue;
    return earlier.role === "user" ? earlier.id : last.id;
  }
  return last.id;
};

/** The id of the last message the list actually renders, or null when none. */
export const lastRenderableMessageId = (messages: Message[]): number | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].contents !== null) return messages[i].id;
  }
  return null;
};
